"use client";

import React from "react";
import { motion } from "framer-motion";
import { UserCheck, Target, MapPin, MessageSquare, SlidersHorizontal as Sliders, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
const differentiators = [{
  id: "1",
  number: "01",
  title: "Personal accountability",
  description: "While traditional agency arrangements often divide tasks across account managers and rotating tiers, you work directly with me from day one. I take direct hands-on responsibility for every strategic recommendation and technical decision.",
  icon: UserCheck,
  tag: "Direct Expert Access"
}, {
  id: "2",
  number: "02",
  title: "Business-focused approach",
  description: "I prioritise qualified leads, customer inquiries, and commercial revenue over vanity traffic metrics. Every recommended activity is evaluated against its true business impact.",
  icon: Target,
  tag: "Outcome Driven"
}, {
  id: "3",
  number: "03",
  title: "UK SME specialisation",
  description: "Deep understanding of UK search intent, customer buying habits, and local competitive landscapes.",
  icon: MapPin,
  tag: "Market Specialist",
  hasCustomLinks: true
}, {
  id: "4",
  number: "04",
  title: "Transparent communication",
  description: "Clear updates, live performance tracking, and frank advisory calls. No black-box reporting, vanity slide decks, or technical jargon designed to obscure real progress.",
  icon: MessageSquare,
  tag: "Zero Fluff"
}, {
  id: "5",
  number: "05",
  title: "Flexible engagement",
  description: "A scalable time commitment shaped around your current growth stage and budget. My involvement scales up during key campaigns or scales down smoothly as your in-house team builds capability.",
  icon: Sliders,
  tag: "Adaptive Model"
}];
export default function Differentiators() {
  return <section id="fractionaldifferentiators" data-nav="light" className="relative bg-background text-foreground py-20 sm:py-28 overflow-hidden">
      {/* Subtle decorative background glow */}
      <div className="absolute top-0 right-1/4 -z-10 w-96 h-96 bg-secondary/40 rounded-full blur-3xl pointer-events-none opacity-60" />
      <div className="absolute bottom-0 left-10 -z-10 w-80 h-80 bg-muted/60 rounded-full blur-3xl pointer-events-none opacity-50" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-16 sm:mb-20">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold gap-1.5 border-border">
              <Sparkles className="size-3 text-accent" />
              <span>How I Work</span>
            </Badge>
          </motion.div>

          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.05,
          ease: "easeOut"
        }} className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold tracking-tight text-foreground">
            What makes my fractional SEO service different?
          </motion.h2>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.55,
          delay: 0.1,
          ease: "easeOut"
        }} className="mt-5 text-lg sm:text-xl text-muted-foreground leading-relaxed">
            Most SEO consultants focus on deliverables — <span className="text-foreground font-medium">I focus on outcomes</span>.
          </motion.p>
        </div>

        {/* 5 Cards Grid + Summary Slot */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {differentiators.map((item, i) => {
          const IconComponent = item.icon;
          const isLargeCard = i === 2; // UK SME card spans two columns on md/lg or styled prominently

          return <motion.div key={item.id} data-index={i} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.08,
            ease: "easeOut"
          }} className={isLargeCard ? "md:col-span-2 lg:col-span-1" : ""}>
                <Card className="h-full border border-border/80 bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 rounded-xl relative overflow-hidden group">
                  {/* Card top accent strip */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-accent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  
                  <CardHeader className="p-6 sm:p-8 pb-4">
                    <div className="flex items-center justify-between gap-4 mb-4">
                      <div className="size-12 rounded-lg bg-secondary/80 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                        <IconComponent className="size-6" />
                      </div>
                      <span className="text-sm font-mono font-semibold text-muted-foreground/60">
                        {item.number}
                      </span>
                    </div>

                    <CardTitle className="text-xl font-serif font-semibold text-card-foreground">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-6 sm:p-8 pt-0">
                    {item.hasCustomLinks ? <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        Deep understanding of UK local search, customer behaviour, legislation and competitive landscapes, whether targeting national keywords for{" "}
                        <Link to="/EcommerceSeoPage" className="font-medium text-primary hover:text-primary/80 underline underline-offset-4 decoration-accent/60 transition-colors">
                          ecommerce
                        </Link>{" "}
                        or local terms for{" "}
                        <Link to="/B2BSeoLeadGenerationPage" className="font-medium text-primary hover:text-primary/80 underline underline-offset-4 decoration-accent/60 transition-colors">
                          B2B lead generation
                        </Link>
                        .
                      </p> : <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                        {item.description}
                      </p>}
                  </CardContent>
                </Card>
              </motion.div>;
        })}

          {/* 6th Slot: Strategic Clarity Summary Box */}
          <motion.div data-index={5} initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.45,
          ease: "easeOut"
        }} className="md:col-span-2 lg:col-span-1">
            <div className="h-full rounded-xl bg-primary text-primary-foreground p-6 sm:p-8 flex flex-col justify-between shadow-sm relative overflow-hidden">
              <div className="relative z-10">
                <Badge variant="secondary" className="bg-primary-foreground/15 text-primary-foreground border-none text-xs mb-4">
                  Fractional Leadership
                </Badge>
                <h3 className="font-serif font-bold text-xl sm:text-2xl text-primary-foreground leading-snug">
                  Senior SEO leadership on demand
                </h3>
                <p className="mt-3 text-primary-foreground/90 text-sm leading-relaxed">
                  High-level strategic steering, technical roadmap governance, and team mentorship tailored to your commercial goals without the executive salary and hiring overhead.
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-primary-foreground/20 relative z-10">
                <Button asChild variant="secondary" size="default" className="w-full justify-between font-medium">
                  <Link to="/ContactPage">
                    <span>Book a free consultation</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>

              {/* Decorative circle glow */}
              <div className="absolute -bottom-10 -right-10 size-40 bg-accent/30 rounded-full blur-2xl pointer-events-none" />
            </div>
          </motion.div>
        </div>

        {/* Closing Statement Callout Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.55,
        delay: 0.2,
        ease: "easeOut"
      }} className="mt-12 sm:mt-16 p-6 sm:p-8 rounded-xl bg-secondary/50 border-l-4 border-primary border-t border-r border-b border-border shadow-2xs">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-1 max-w-3xl">
              <p className="text-base sm:text-lg font-serif font-medium text-foreground leading-relaxed">
                Whether embedding within an established marketing function or guiding executive decisions, I provide clear, self-directed fractional SEO leadership.
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Senior UK SEO consultancy tailored to your business growth and revenue goals.
              </p>
            </div>

            <div className="shrink-0">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/CaseStudiesPage">
                  <span>Explore my case studies</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}